export function roundMoney(value: number | string | null | undefined): number {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

/** Bill-level (cashier) discount only — line discounts belong on Sale Details. */
export function getBillLevelDiscount(bill: {
  givenDiscount?: number | string | null;
}): number {
  return roundMoney(Number(bill.givenDiscount || 0));
}

export function getBillDiscountTotal(bill: {
  givenDiscount?: number | string | null;
}): number {
  return getBillLevelDiscount(bill);
}

/**
 * Amount due after bill-level discount.
 * Item/line discounts are already reflected in `total` / `netTotal`.
 */
export function getLineTaxableBase(product: {
  price?: number | string | null;
  lineDiscount?: number | string | null;
  quantity?: number | string | null;
}): number {
  const qty = Number(product.quantity || 0);
  const price = Number(product.price || 0);
  const discount = Number(product.lineDiscount || 0);
  return roundMoney(Math.max(0, (price - discount) * qty));
}

/**
 * Amount due after bill-level discount (before returns).
 * Prefer `total` over report `netTotal` so Sales-report netTotal (already
 * after returns) is not double-adjusted by givenDiscount.
 */
export function getBillPayable(bill: {
  total?: number | string | null;
  netTotal?: number | string | null;
  givenDiscount?: number | string | null;
}): number {
  const total = Number(bill.total ?? bill.netTotal ?? 0);
  return roundMoney(Math.max(0, total - Number(bill.givenDiscount || 0)));
}

/**
 * Amount due after bill discount and returns.
 * Sales report may send `netTotal` already net of both.
 */
export function getBillNetPayable(bill: {
  total?: number | string | null;
  netTotal?: number | string | null;
  givenDiscount?: number | string | null;
  returnedAmount?: number | string | null;
}): number {
  if (bill.netTotal != null && bill.netTotal !== "") {
    return roundMoney(Number(bill.netTotal));
  }
  return roundMoney(
    Math.max(0, getBillPayable(bill) - Number(bill.returnedAmount || 0)),
  );
}

/**
 * Cash column for a bill: cash share of the amount due.
 * Do not subtract item `discount` again — it is already in `total`.
 */
export function getBillCashAmount(bill: {
  isCreditSale?: boolean;
  hasUnpaidAmount?: boolean;
  cash?: number | string | null;
  total?: number | string | null;
  netTotal?: number | string | null;
  card?: number | string | null;
  bank?: number | string | null;
  givenDiscount?: number | string | null;
  returnedAmount?: number | string | null;
}): number {
  if (bill.isCreditSale && bill.hasUnpaidAmount) {
    return roundMoney(Number(bill.cash || 0));
  }
  return roundMoney(
    Math.max(
      0,
      getBillNetPayable(bill) - Number(bill.card || 0) - Number(bill.bank || 0),
    ),
  );
}

export function getChangeGiven(bill: {
  amountRecived?: number | string | null;
  amountReceived?: number | string | null;
  cash?: number | string | null;
  card?: number | string | null;
  bank?: number | string | null;
  netTotal?: number | string | null;
  total?: number | string | null;
  givenDiscount?: number | string | null;
}): number {
  const tendered = roundMoney(
    Number(
      bill.amountRecived ??
        bill.amountReceived ??
        Number(bill.cash || 0) +
          Number(bill.card || 0) +
          Number(bill.bank || 0),
    ),
  );
  return roundMoney(Math.max(0, tendered - getBillPayable(bill)));
}
